'use client';
import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import Icon from '@/components/ui/AppIcon';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/submit-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (_) {
      setSubmitError('Something went wrong. Please try again or email us directly at hello@bestoddscorp.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="Get In Touch"
                title="Let's Find Your Bottleneck"
                description="Not sure where to start? Most conversations begin with a Sprint — a paid diagnostic that maps your constraints and delivers a clear execution roadmap. Reach out and we'll figure out the right path together."
                align="center"
                className="mb-16"
              />
            </div>
          </div>
        </section>

        {/* Contact Info & Form */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Information */}
              <div>
                <div className="reveal mb-12">
                  <h2 className="text-3xl font-bold mb-6">Start the Conversation</h2>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">
                    Whether you want to book a Sprint, explore a buildout, discuss ongoing execution, or just ask a question — we're here. No pressure to commit to a package before we understand your situation.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="reveal flex items-start gap-4">
                    <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                      <Icon name="MapPinIcon" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-label uppercase tracking-widest text-foreground mb-2">Location</h3>
                      <p className="text-body-sm text-muted-foreground">Las Vegas, Nevada</p>
                      <p className="text-body-sm text-muted-foreground">United States</p>
                    </div>
                  </div>

                  <div className="reveal flex items-start gap-4">
                    <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                      <Icon name="EnvelopeIcon" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-label uppercase tracking-widest text-foreground mb-2">Email</h3>
                      <a href="mailto:hello@bestoddscorp.com" className="text-body-sm text-muted-foreground hover:text-primary transition-colors">
                        hello@bestoddscorp.com
                      </a>
                    </div>
                  </div>

                  <div className="reveal flex items-start gap-4">
                    <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                      <Icon name="PhoneIcon" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-label uppercase tracking-widest text-foreground mb-2">Phone</h3>
                      <a href="tel:+17025466337" className="text-body-sm text-muted-foreground hover:text-primary transition-colors">
                        (702) 546-6337
                      </a>
                    </div>
                  </div>

                  <div className="reveal flex items-start gap-4">
                    <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                      <Icon name="ClockIcon" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-label uppercase tracking-widest text-foreground mb-2">Hours</h3>
                      <p className="text-body-sm text-muted-foreground">Monday - Friday</p>
                      <p className="text-body-sm text-muted-foreground">9:00 AM - 6:00 PM PST</p>
                    </div>
                  </div>
                </div>

                {/* Inquiry path options */}
                <div className="reveal mt-12 p-6 border border-border bg-surface-1">
                  <h3 className="text-label uppercase tracking-widest text-foreground mb-4">What are you looking for?</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Book a Sprint / Diagnostic', href: '/book' },
                      { label: 'Explore a Buildout or Project', href: '/pricing' },
                      { label: 'Discuss Ongoing Execution', href: '/pricing' },
                      { label: 'Browse DIY Resources', href: '/diy' },
                    ].map((item) => (
                      <a key={item.label} href={item.href} className="flex items-center gap-2 text-body-sm text-muted-foreground hover:text-primary transition-colors group">
                        <span className="text-primary group-hover:translate-x-1 transition-transform">→</span>
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div className="reveal mt-8">
                  <h3 className="text-label uppercase tracking-widest text-foreground mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a
                      href="https://www.linkedin.com/company/best-odds-corp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="LinkedIn"
                    >
                      <Icon name="BuildingOfficeIcon" size={18} />
                    </a>
                    <a
                      href="https://twitter.com/bestoddscorp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="Twitter / X"
                    >
                      <Icon name="ChatBubbleLeftIcon" size={18} />
                    </a>
                    <a
                      href="https://www.facebook.com/bestoddscorp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="Facebook"
                    >
                      <Icon name="GlobeAltIcon" size={18} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                {submitSuccess ? (
                  <div className="border border-border bg-surface-1 p-8">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3 font-semibold">Message Sent</p>
                    <h2 className="text-2xl font-bold mb-3">We got your message</h2>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      Expect a reply within 1 business day. We review every message personally and will get back to you with a thoughtful response.
                    </p>
                    <button
                      onClick={() => setSubmitSuccess(false)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="reveal">
                      <Input
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div className="reveal">
                      <Input
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div className="reveal">
                      <Input
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Sprint inquiry, project question, general..."
                        required
                      />
                    </div>

                    <div className="reveal">
                      <Textarea
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your business and what you're trying to solve..."
                        rows={6}
                        required
                      />
                    </div>

                    {submitError && (
                      <p className="text-red-400 text-sm">{submitError}</p>
                    )}

                    <div className="reveal">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}